import {
    Button, Checkbox, CircularProgress,
    Dialog, DialogActions,
    DialogContent,
    DialogTitle, FormControlLabel, FormGroup, Grid,
    Typography
} from "@material-ui/core";
import React, {useRef, useState} from "react";
import QRCodeScanner from "../components/QRCodeScanner";
import QRCodeTextField from "../components/QRCodeTextField";
import {columnOffsets} from "../Config";

const CLONE_PROPERTIES = [
    "copy_name", "copy_sounders", "copy_components", "copy_install_photos", "copy_vehicle_id", "copy_vehicle_photos",
    "copy_customer_and_site", "copy_icon", "copy_notes", "copy_tags"
].map(name => {
    return {
        name: name,
        label: name.replaceAll(
            /(?:^|_)(.)/g, char => char.toUpperCase()
        ).replaceAll('_', ' ')
    }
})

function Scanning({onChange, qrCode, invalid}){
    const [scanning, setScanning] = useState(false)

    return <DialogContent>
        <Typography variant="body1">
            Please scan or enter the QR code or device ID of the new master
        </Typography>
        <QRCodeTextField
            value={qrCode}
            onChange={e => onChange(e.target.value)}
            onToggleScanner={() => setScanning(true)}
            error={invalid}
            helperText={invalid ? 'Invalid code' : null}
            label="QR Code or Device ID"
        />
        {scanning ? <QRCodeScanner
            formats={['qr_code']}
            onResult={result => {
                setScanning(false)
                onChange(result.qr_code)
            }}
            onClose={() => setScanning(false)}
        /> : null}
    </DialogContent>
}

function Configuring({flags, deviceName}){
    const [redraw, setRedraw] = useState(false)

    const onChange = (e, checked) => {
        const {target} = e
        flags[target.name] = checked
        setRedraw(!redraw)
    }

    return <DialogContent>
        <Typography variant="body1">with <b>{deviceName}</b></Typography>
        <FormGroup>
            {CLONE_PROPERTIES.map(({name, label}) => {
                return <FormControlLabel
                    control={<Checkbox onChange={onChange} name={name} checked={flags[name]}/>}
                    label={label}
                    key={name}
                />
            })}
        </FormGroup>
    </DialogContent>
}

function Waiting({message}){
    return <DialogContent>
        <Grid container spacing={2}>
            <Grid container item justify="center" xs={12}>
                <CircularProgress/>
            </Grid>
            <Grid container item justify="center" xs={12}>
                <Typography variant="caption">{message}...</Typography>
            </Grid>
        </Grid>
    </DialogContent>
}

const createInitialState = () => CLONE_PROPERTIES.reduce((set, {name}) => {
    set[name] = true
    return set
}, {})

const {ID, SERIAL, NAME, QR_CODE, CODE} = columnOffsets
const SCANNING = 0, SEARCHING = 1, CONFIGURING = 2, SAVING = 3

export default function ReplaceMasterDialog({open, onComplete, onCancel, deviceId, devicesContext}){
    const [state, setState] = useState(SCANNING)
    const [qrCode, setQrCode] = useState('')
    const [newDevice, setNewDevice] = useState(null)
    const [flags, setFlags] = useState(createInitialState)

    const rejected = useRef(new Set())

    function clear() {
        setFlags(createInitialState())
        setState(SCANNING)
        setQrCode('')
    }

    function getDeviceName(){
        const name = newDevice[NAME], serial = newDevice[SERIAL]
        return name && /\S+/.test(name) ? `${name} (${serial})` : serial
    }

    async function search(){
        setState(SEARCHING)
        const devices = await devicesContext.fetchDevices()
        const device = devices.list.find(
            item => item[QR_CODE] === qrCode || item[CODE] === qrCode || item[SERIAL] === qrCode
        )
        if (device){
            setNewDevice(device)
            setState(CONFIGURING)
        }
        else{
            rejected.current.add(qrCode)
            setState(SCANNING)
        }
    }

    async function save(){
        setState(SAVING)
        onComplete(await devicesContext.replaceMaster(deviceId, newDevice[ID], flags))
        clear()
    }

    const invalid = rejected.current.has(qrCode)

    function buttons(disabled, onNext){
        return <DialogActions>
            <Button variant="outlined" onClick={() => {
                clear()
                onCancel()
            }}>
                Cancel
            </Button>
            <Button
                variant="contained"
                disabled={disabled}
                onClick={onNext}
            >
                Next
            </Button>
        </DialogActions>
    }

    function content(){
        switch (state){
            case SCANNING:
                return <><Scanning qrCode={qrCode} onChange={setQrCode} invalid={invalid}/>{buttons(
                    invalid || !/\S+/.test(qrCode), search
                )}</>
            case SEARCHING:
                return <Waiting message="Searching" />
            case CONFIGURING:
                return <><Configuring flags={flags} deviceName={getDeviceName()}/>{buttons(false, save)}</>
            default:
                return <Waiting message="Saving" />
        }
    }

    return <Dialog open={open}>
        <DialogTitle>Replace Master</DialogTitle>
        {content()}
    </Dialog>
}
